// skeleton/app/src/services/Auth/SiteminderOIDCAuthService.ts

import { AuthService } from "./AuthService";
import { User } from "oidc-client-ts";
import { fallbackConfig, oidcConfig } from "../../auth/config";
import { oidcUserManager } from "../../auth/userManager";
import { AppUser } from "../../Models/AppUser";
import httpService from "../httpService";
import { ApiEndpoints } from "../../constants/appConstants";
import { jwtDecode } from "jwt-decode";

interface DecodedExp {
  exp: number;
}

export class SiteminderOIDCAuthService implements AuthService {
  constructor() {
    console.log("SiteminderOIDCAuthService initialized with config:");
    console.log("Authority:", oidcConfig.authority);
    console.log("Redirect URI:", oidcConfig.redirect_uri);
    console.log("Silent Redirect URI:", oidcConfig.silent_redirect_uri);
    console.log("Post Logout URI:", oidcConfig.post_logout_redirect_uri);
    console.log("Requested Scope:", oidcConfig.scope);
    console.log("OIDC Metadata loaded:", oidcConfig.metadata);
  }

  async login(): Promise<void> {
    console.debug("login(): Redirecting to SiteMinder login...");
    console.debug("Authority:", oidcConfig.authority);
    console.debug("Redirect URI:", oidcConfig.redirect_uri);
    console.log("login(): Redirecting to SiteMinder login...");
    console.log("Authority:", oidcConfig.authority);
    console.log("Redirect URI:", oidcConfig.redirect_uri);
    await oidcUserManager.signinRedirect();
  }

  async loginWithoutCredentials(): Promise<boolean> {
    console.debug("loginWithoutCredentials(): Starting login without credentials...");
    console.log("loginWithoutCredentials(): Starting login without credentials...");
    console.log("OIDC authority:", oidcConfig.authority);
    console.log("OIDC redirect_uri:", oidcConfig.redirect_uri);
    console.log("OIDC client_id:", oidcConfig.client_id);

    const loginDeniedFlag = sessionStorage.getItem("loginDenied");

    if (loginDeniedFlag === "true") {
      console.warn("[SiteminderOIDCAuthService] Skipping auto-login: previous access_denied detected.");
      console.warn("[SiteminderOIDCAuthService] SiteMinder will deny again if retried without user re-login.");
      return false;
    }

    try {
      console.log("[SiteminderOIDCAuthService] Triggering redirect to SiteMinder login...");
      await oidcUserManager.signinRedirect();
      console.debug("[SiteminderOIDCAuthService] Redirect triggered.");
      console.log("[SiteminderOIDCAuthService] Redirect triggered.");
      return true;
    } catch (error) {
      console.error("[SiteminderOIDCAuthService] Login redirect failed", error);
      return false;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUserInternal();
    return user != null && !user.expired;
  }

  async logout(): Promise<void> {
    console.log("[SiteminderOIDCAuthService] - Logout()");

    try {
      console.log("[SiteminderOIDCAuthService] - Calling API Logout...");
      await httpService.post(ApiEndpoints.Logout, {});
      console.log("[SiteminderOIDCAuthService] - API Logout completed.");
    } catch (err) {
      console.error("[SiteminderOIDCAuthService] - API Logout failed (continuing client-side logout).", err);
    }

    const id_token = sessionStorage.getItem("id_token");

    try {
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("id_token");
      sessionStorage.removeItem("authorizationToken");
      sessionStorage.removeItem("authorizationTokenExp");
      sessionStorage.removeItem("accessTokenExp");
      sessionStorage.removeItem("authenticated");
      sessionStorage.removeItem("loginDenied");
      sessionStorage.removeItem("has_refresh_token");
      console.log("[SiteminderOIDCAuthService] - Session storage auth keys cleared.");
    } catch (err) {
      console.error("[SiteminderOIDCAuthService] - Error clearing storage.", err);
    }

    try {
      await oidcUserManager.removeUser();
    } catch (err) {
      console.warn("[SiteminderOIDCAuthService] removeUser failed.", err);
    }

    try {
      await oidcUserManager.clearStaleState();
    } catch (err) {
      console.warn("[SiteminderOIDCAuthService] clearStaleState failed.", err);
    }

    const loginUrl = oidcConfig.metadata?.authorization_endpoint ?? "";
    const fallbackLoginUrl = fallbackConfig.siteMinderLoginRedirectUrl;

    try {
      await oidcUserManager.signoutRedirect({
        post_logout_redirect_uri: oidcConfig.post_logout_redirect_uri,
        id_token_hint: id_token ?? undefined,
      });
      console.log("[SiteminderOIDCAuthService] - signoutRedirect initiated.");
    } catch (err) {
      console.warn("OIDC signoutRedirect failed. Falling back to manual redirect:", err);

      console.debug("Logging out - redirecting to:", loginUrl);
      console.debug("post_logout_redirect_uri:", oidcConfig.post_logout_redirect_uri);
      console.debug("id_token_hint:", id_token?.substring(0, 20) + "...");
      console.log("Logging out - redirecting to:", loginUrl);
      console.log("post_logout_redirect_uri:", oidcConfig.post_logout_redirect_uri);
      console.log("id_token_hint:", id_token?.substring(0, 20) + "...");

      if (loginUrl) {
        window.location.href = `${loginUrl}?client_id=${oidcConfig.client_id}&response_type=code&scope=${encodeURIComponent(
          oidcConfig.scope || "openid profile"
        )}&redirect_uri=${encodeURIComponent(oidcConfig.redirect_uri || "")}`;
      } else if (fallbackLoginUrl) {
        window.location.href = fallbackLoginUrl;
      } else {
        console.error("No login URL available for fallback redirect.");
      }
    }
  }

  private getTokenSecondsLeft(token: string | null): number | null {
    if (!token) {
      return null;
    }

    try {
      const decoded: DecodedExp = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp - now;
    } catch (err) {
      console.error("[SiteminderOIDCAuthService] Failed to decode token exp", err);
      return null;
    }
  }

  private syncSessionStorageFromUser(user: User | null): void {
    if (!user || user.expired) {
      sessionStorage.removeItem("authenticated");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("id_token");
      sessionStorage.removeItem("accessTokenExp");
      sessionStorage.removeItem("has_refresh_token");
      return;
    }

    sessionStorage.setItem("authenticated", "true");

    if (user.access_token) {
      sessionStorage.setItem("access_token", user.access_token);

      try {
        const decoded: DecodedExp = jwtDecode(user.access_token);
        sessionStorage.setItem("accessTokenExp", String(decoded.exp));
        console.log("[SiteminderOIDCAuthService] Stored accessTokenExp:", decoded.exp);
      } catch (decodeErr) {
        console.error("[SiteminderOIDCAuthService] Failed to decode access token exp", decodeErr);
      }
    }

    if (user.id_token) {
      sessionStorage.setItem("id_token", user.id_token);
    }

    if (user.refresh_token) {
      sessionStorage.setItem("has_refresh_token", "true");
    } else {
      sessionStorage.removeItem("has_refresh_token");
    }

    console.log("[SiteminderOIDCAuthService] Refresh token available on user:", !!user.refresh_token);
  }

  private async tryRescueExpiredSession(
    currentUser: User | null
  ): Promise<User | null> {
    try {
      if (!currentUser) {
        console.warn("[SiteminderOIDCAuthService] tryRescueExpiredSession(): current user is null.");
        return null;
      }

      console.warn("[SiteminderOIDCAuthService] Current OIDC user is expired. Attempting silent rescue renew.");

      const rescuedUser = await oidcUserManager.signinSilent();

      if (!rescuedUser || rescuedUser.expired) {
        console.warn("[SiteminderOIDCAuthService] Silent rescue renew did not return a valid user.");
        return null;
      }

      this.syncSessionStorageFromUser(rescuedUser);

      console.log("[SiteminderOIDCAuthService] Silent rescue renew succeeded.", {
        hasRefreshToken: !!rescuedUser.refresh_token,
        accessTokenPreview: rescuedUser.access_token?.substring(0, 20) + "...",
      });

      return rescuedUser;
    } catch (rescueErr) {
      console.error("[SiteminderOIDCAuthService] Silent rescue renew failed.", rescueErr);
      return null;
    }
  }

  async hasActiveSession(): Promise<boolean> {
    let user = await this.getUserInternal();

    if (user && user.expired) {
      const rescuedUser = await this.tryRescueExpiredSession(user);
      if (rescuedUser) {
        user = rescuedUser;
      }
    }

    const isValid = user !== null && !user.expired;

    console.debug("Session Valid:", isValid);
    console.log("Session Valid:", isValid);

    if (user) {
      console.debug("Access Token:", user.access_token?.substring(0, 30) + "...");
      console.debug("ID Token:", user.id_token?.substring(0, 30) + "...");
      console.debug("Refresh Token Present:", !!user.refresh_token);
      console.debug("Token Expired:", user.expired);

      console.log("Access Token:", user.access_token?.substring(0, 30) + "...");
      console.log("ID Token:", user.id_token?.substring(0, 30) + "...");
      console.log("Refresh Token Present:", !!user.refresh_token);
      console.log("Token Expired:", user.expired);

      const realAccessTokenSecondsLeft = this.getTokenSecondsLeft(
        user.access_token || null
      );
      console.log(
        "[SiteminderOIDCAuthService] Real access token seconds left:",
        realAccessTokenSecondsLeft
      );
    } else {
      console.warn("No user retrieved during session check.");
    }

    if (isValid) {
      this.syncSessionStorageFromUser(user);
    } else {
      sessionStorage.removeItem("authenticated");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("id_token");
      sessionStorage.removeItem("authorizationToken");
      sessionStorage.removeItem("authorizationTokenExp");
      sessionStorage.removeItem("accessTokenExp");
      sessionStorage.removeItem("has_refresh_token");
      console.debug("Cleared sessionStorage auth keys due to invalid session");
      console.log("Cleared sessionStorage auth keys due to invalid session");
    }

    return isValid;
  }

  async getUser(): Promise<AppUser | null> {
    let user = await this.getUserInternal();

    if (user && user.expired) {
      const rescuedUser = await this.tryRescueExpiredSession(user);
      if (rescuedUser) {
        user = rescuedUser;
      }
    }

    if (user && !user.expired) {
      console.log("[SiteminderOIDCAuthService] Valid OIDC user found in getUser().");

      const existingAuthorizationToken =
        sessionStorage.getItem("authorizationToken") ?? "";
      const existingBackendSecondsLeft =
        this.getTokenSecondsLeft(existingAuthorizationToken);

      if (
        !existingAuthorizationToken ||
        existingBackendSecondsLeft === null ||
        existingBackendSecondsLeft <= 60
      ) {
        console.log("[SiteminderOIDCAuthService] Backend authorizationToken missing/expiring soon. Fetching it now.");
        await this.getAuthorizationToken();
      } else {
        console.log("[SiteminderOIDCAuthService] Reusing existing backend authorizationToken. Skipping duplicate fetch.");
      }

      const authorizationToken = sessionStorage.getItem("authorizationToken") ?? "";
      const roles = this.getRolesFromAuthorizationToken(authorizationToken);

      const appUser: AppUser = {
        name: user.profile.name ?? "",
        email: user.profile.email ?? "",
        groups: roles,
      };

      console.debug("Decoded AppUser:", {
        name: appUser.name,
        email: appUser.email,
        roles: appUser.groups,
        hasRefreshToken: !!user.refresh_token,
      });
      console.log("AppUser created from OIDC:", appUser);
      console.debug("AppUser created from OIDC:", appUser);

      return appUser;
    }

    console.warn("No valid user found in getUser()");
    return null;
  }

  private async getUserInternal(): Promise<User | null> {
    try {
      const latestUser = await oidcUserManager.getUser();

      if (!latestUser) {
        console.warn("[SiteminderOIDCAuthService] getUserInternal(): no user found in oidc storage.");
        return null;
      }

      this.syncSessionStorageFromUser(latestUser);

      console.log(
        "[SiteminderOIDCAuthService] getUserInternal(): latest user loaded from oidc storage.",
        {
          expired: latestUser.expired,
          accessTokenPreview: latestUser.access_token?.substring(0, 20) + "...",
          hasRefreshToken: !!latestUser.refresh_token,
          scope: latestUser.scope,
        }
      );

      return latestUser;
    } catch (error) {
      console.error("Failed to get user", error);
      return null;
    }
  }

  async getAuthorizationToken(): Promise<void> {
    try {
      console.log(
        "[SiteminderOIDCAuthService] Fetching authorization token with access_token:",
        sessionStorage.getItem("access_token")?.substring(0, 30) + "..."
      );

      const response = await httpService.get<any>(ApiEndpoints.getAuthorizationToken);
      const backend = response?.data?.Token ?? response?.data?.token;

      if (backend && backend.length > 0) {
        sessionStorage.setItem("authorizationToken", backend);
        sessionStorage.setItem("authenticated", "true");

        try {
          const decoded: { exp: number } = jwtDecode(backend);
          sessionStorage.setItem("authorizationTokenExp", String(decoded.exp));
          console.log("[SiteminderOIDCAuthService] Stored authorizationTokenExp:", decoded.exp);
        } catch (decodeErr) {
          console.error("[SiteminderOIDCAuthService] Failed to decode backend authorization token exp", decodeErr);
        }

        console.debug("Authorization token stored in sessionStorage");
        console.log("Authorization token stored in sessionStorage");
      } else {
        console.warn("No token returned from API", response?.data);
      }
    } catch (error) {
      console.error("Error fetching authorization token:", error);
    }
  }

  getRolesFromAuthorizationToken(token: string): string[] {
    if (!token) return [];

    try {
      const payload: any = jwtDecode(token);
      let roles = payload?.role ?? [];

      if (!Array.isArray(roles)) {
        roles = roles ? [roles] : [];
      }

      return roles;
    } catch (err) {
      console.error("Failed to decode JWT payload:", err);
      return [];
    }
  }
}